import { Component, OnInit } from '@angular/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { FlowManagementService } from '../services/flow-management.service';

type FeedbackMode = 'feedback' | 'bug';
type FeedbackType = 'bug' | 'feature' | 'general' | 'other';

@Component({
    selector: 'app-feedback',
    templateUrl: './feedback.component.html',
    styleUrls: ['./feedback.component.scss'],
    standalone: false
})
export class FeedbackComponent implements OnInit {

  public mode: FeedbackMode = 'feedback';
  public feedbackType: FeedbackType = 'general';
  public readonly maxChars = 500;
  public attachmentName = '';
  public attachmentInfo = '';
  public attachmentFile: File | null = null;
  public feedbackText = '';

  /** DataURL of the pre-dialog screenshot, set by the parent before opening. */
  public screenshotDataUrl: string | null = null;
  /** Whether the user has chosen to attach the pre-captured screenshot. */
  public screenshotAttached = false;

  constructor(
    public dialog: DialogRef,
    private flowManagementService: FlowManagementService
  ) {}

  private readonly feedbackTemplate = '';

  private readonly featureTemplate =
`What would you like to see? What tool would really help you?

Describe the feature:

Why is it important to you:

Any examples or references (optional):
`;

  private readonly generalTemplate =
`What is your favourite feature?

`;

  private readonly otherTemplate =
`Ask Flow Ninja anything!

`;

  private readonly bugTemplate =
`summary:

steps to reproduce:
1.
2.
3.

expected result:

actual result:

environment details:
- browser:
- tenant:
- flow/environment name (optional):
`;

  ngOnInit(): void {
    this.setMode(this.mode);
  }

  public setMode(mode: FeedbackMode): void {
    this.mode = mode;
    this.feedbackType = mode === 'bug' ? 'bug' : 'general';
    if (!this.feedbackText || this.feedbackText.trim().length === 0) {
      this.feedbackText = mode === 'bug' ? this.bugTemplate : this.feedbackTemplate;
    }
  }

  /** Called by the parent (app.component) after opening the dialog. */
  public setScreenshot(dataUrl: string): void {
    this.screenshotDataUrl = dataUrl;
    this.screenshotAttached = true;
    // Prefer the auto-captured screenshot by default.
    this.attachmentFile = null;
    this.attachmentName = '';
    this.attachmentInfo = '';
  }

  public attachScreenshot(): void {
    this.screenshotAttached = true;
    // Clear any manually-picked file when the screenshot is chosen
    this.attachmentFile = null;
    this.attachmentName = '';
    this.attachmentInfo = '';
  }

  public removeScreenshot(): void {
    this.screenshotAttached = false;
  }

  public selectType(type: FeedbackType): void {
    this.feedbackType = type;
    if (type === 'feature') {
      this.feedbackText = this.featureTemplate;
    } else if (type === 'general') {
      this.feedbackText = this.generalTemplate;
    } else if (type === 'other') {
      this.feedbackText = this.otherTemplate;
    } else if (type === 'bug') {
      this.feedbackText = this.bugTemplate;
    }
  }

  public onFeedbackTextChange(value: string): void {
    if (value.length <= this.maxChars) {
      this.feedbackText = value;
      return;
    }

    this.feedbackText = value.slice(0, this.maxChars);
  }

  public onAttachmentPicked(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      this.attachmentFile = null;
      this.attachmentName = '';
      this.attachmentInfo = '';
      if (this.screenshotDataUrl) {
        this.screenshotAttached = true;
      }
      return;
    }

    this.attachmentFile = file;
    this.attachmentName = file.name;
    const sizeMb = file.size / (1024 * 1024);
    this.attachmentInfo = `${sizeMb.toFixed(2)} MB`;
    // A manually-picked file takes priority; clear pre-captured screenshot attachment
    this.screenshotAttached = false;
  }

  public get helperText(): string {
    return 'Help us improve FlowStudio';
  }

  public get charsUsed(): number {
    return this.feedbackText.length;
  }

  public get canSubmit(): boolean {
    return this.feedbackText.trim().length > 0;
  }

  public get selectedTypeLabel(): string {
    if (this.feedbackType === 'bug') return 'Bug Report';
    if (this.feedbackType === 'feature') return 'Feature';
    if (this.feedbackType === 'other') return 'Anything';
    return 'General';
  }

  public async greatFeedback(): Promise<void> {
    const screenshotToSend = this.screenshotAttached ? this.screenshotDataUrl : null;

    const attachmentLine = this.attachmentName
      ? `\nAttachment: ${this.attachmentName}${this.attachmentInfo ? ` (${this.attachmentInfo})` : ''}`
      : (screenshotToSend ? '\nAttachment: screenshot.png (captured)' : '');
    const payload = `[${this.selectedTypeLabel}]\n${this.feedbackText}${attachmentLine}`;

    let attachment: { content: string; name: string } | undefined;
    if (this.attachmentFile) {
      try {
        const content = await this.readFileAsBase64(this.attachmentFile);
        attachment = { content, name: this.attachmentFile.name };
      } catch (error) {
        // File attachment is optional; submit feedback without it when conversion fails.
        console.warn('[Feedback] Unable to encode attachment image.', error);
      }
    }

    this.flowManagementService.feedback(payload, screenshotToSend ?? undefined, attachment).then(() => {
      this.close();
    });
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1] || result);
      };
      reader.onerror = () => reject(new Error(`Failed to read attachment: ${file.name}`));
      reader.readAsDataURL(file);
    });
  }

  public close(): void {
    if (this.dialog) {
      this.dialog.close();
    }
  }
}
